import { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Typography, Alert, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore as ExpandIcon } from '@mui/icons-material';
import { TextBlock } from '../../../Atoms/Text';
import { useEngagementEditor } from './EngagementEditorContext';
import { getProductType } from '../../../../constants/advise/productTypes';
import { calcSupplierRoi, SupplierRoiResult } from './RoiAnalysisPanel.calc';
import { roiMonthlySpend, typedMonthlySpend, withTypedMonthlySpend } from './currentMonthlyCxSpend.calc';
import { CurrentMonthlyCxSpendField } from './CurrentMonthlyCxSpendField';
import { RoiSummaryCards } from './RoiSummaryCards';
import { RoiFieldGrid } from './RoiFieldGrid';
import { METRICS_FIELDS, SUPPLIER_PROPOSAL_FIELDS } from './roiFields';

interface RoiState {
  currentSpend: Record<string, number>;
  metrics: Record<string, number>;
  supplierProposals: Record<string, Record<string, number>>;
}

const asAnalysis = (state: RoiState) => state as unknown as Record<string, unknown>;
const notSaved = async () => false;

export interface RoiAnalysisPanelProps {
  // Left out where the Current Suppliers tab isn't shown.
  onGoToCurrentSuppliers?: () => void;
}

export const RoiAnalysisPanel = ({ onGoToCurrentSuppliers }: RoiAnalysisPanelProps) => {
  const { engagement, suppliers, canEdit, editEngagement, currentSpend } = useEngagementEditor();
  const selectedSuppliers = useMemo(
    () => suppliers.filter((v) => engagement.selected_suppliers?.includes(v.id)),
    [suppliers, engagement.selected_suppliers]
  );

  const productType = useMemo(() => getProductType(engagement.product_type), [engagement.product_type]);

  const roiData = (engagement.roi_analysis || {}) as unknown as RoiState;

  // Read once, so our own saves never clobber edits; a different engagement remounts this panel.
  const [localState, setLocalState] = useState<RoiState>({
    currentSpend: roiData.currentSpend || {},
    metrics: roiData.metrics || {},
    supplierProposals: roiData.supplierProposals || {}
  });

  // Current Suppliers may have changed since the engagement loaded, here or by a teammate.
  const refreshCurrentSpend = currentSpend?.refresh;
  useEffect(() => {
    refreshCurrentSpend?.();
  }, [refreshCurrentSpend]);

  // TODO(UPDEV-8042): clamp ROI numeric inputs — reject negatives and cap *_pct fields at 100 before they feed supplier totals.
  const handleFieldChange = useCallback((key: string, value: string) => {
    setLocalState((prev) => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [key]: parseFloat(value) || 0
      }
    }));
  }, []);

  const handleSupplierFieldChange = useCallback((supplierId: string, key: string, value: string) => {
    setLocalState((prev) => ({
      ...prev,
      supplierProposals: {
        ...prev.supplierProposals,
        [supplierId]: {
          ...(prev.supplierProposals[supplierId] || {}),
          [key]: parseFloat(value) || 0
        }
      }
    }));
  }, []);

  const handleBlur = useCallback(() => {
    editEngagement({ roi_analysis: asAnalysis(localState) });
  }, [localState, editEngagement]);

  const handleSpendTyped = useCallback(
    (amount: number | null) => {
      const next = withTypedMonthlySpend(asAnalysis(localState), amount) as unknown as RoiState;
      setLocalState(next);
      editEngagement({ roi_analysis: asAnalysis(next) });
    },
    [localState, editEngagement]
  );

  const supplierRoi = useMemo(() => {
    const results: Record<string, SupplierRoiResult> = {};
    const currentMonthlySpend = roiMonthlySpend({
      current_spend: engagement.current_spend,
      roi_analysis: asAnalysis(localState)
    });
    selectedSuppliers.forEach((v) => {
      results[v.id] = calcSupplierRoi(
        currentMonthlySpend,
        localState.metrics,
        localState.supplierProposals[v.id] || {}
      );
    });
    return results;
  }, [localState, selectedSuppliers, engagement.current_spend]);

  if (!productType) {
    return <Alert severity="info">No product type configured.</Alert>;
  }

  if (selectedSuppliers.length === 0) {
    return <Alert severity="info">No suppliers selected for ROI analysis.</Alert>;
  }

  return (
    <Box className="flex flex-col gap-4">
      <RoiSummaryCards
        suppliers={selectedSuppliers}
        results={supplierRoi}
      />

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="subtitle2">Current State Inputs</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextBlock
            size="base"
            weight="semibold"
            color="grey8"
            className="mb-2">
            Financials
          </TextBlock>
          <CurrentMonthlyCxSpendField
            spend={engagement.current_spend}
            typed={typedMonthlySpend(asAnalysis(localState))}
            canEdit={canEdit}
            onType={handleSpendTyped}
            onOverride={currentSpend?.override ?? notSaved}
            onReset={currentSpend?.reset ?? notSaved}
            onGoToCurrentSuppliers={onGoToCurrentSuppliers}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="subtitle2">Agent & Team Metrics</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RoiFieldGrid
            fields={METRICS_FIELDS}
            values={localState.metrics}
            canEdit={canEdit}
            onChange={handleFieldChange}
            onBlur={handleBlur}
          />
        </AccordionDetails>
      </Accordion>

      {selectedSuppliers.map((supplier) => (
        <Accordion key={supplier.id}>
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Typography variant="subtitle2">{supplier.supplier_name} — Proposal Inputs</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <RoiFieldGrid
              fields={SUPPLIER_PROPOSAL_FIELDS}
              values={localState.supplierProposals[supplier.id] || {}}
              canEdit={canEdit}
              onChange={(key, value) => handleSupplierFieldChange(supplier.id, key, value)}
              onBlur={handleBlur}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
